import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  poster: string;
  title: string;
  episodeNumber: number;
}

export default function VideoPlayer({ poster, title, episodeNumber }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(12);
  const totalSeconds = 2712; // 45:12 演示时长
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (playing) {
      timerRef.current = window.setInterval(() => {
        setProgress((p) => {
          if (p >= 100) {
            setPlaying(false);
            return 0;
          }
          return p + 100 / totalSeconds;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing]);

  const formatTime = (ratio: number) => {
    const secs = Math.floor((ratio / 100) * totalSeconds);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
        <img
          src={poster}
          alt={`善良的小姨子 第${episodeNumber}集 播放画面`}
          title={`善良的小姨子 第${episodeNumber}集 在线播放`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-foreground-950/60"></div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            aria-label={playing ? "暂停" : "播放"}
            className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 text-background-50 flex items-center justify-center hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl md:text-4xl`}></i>
          </button>
          <p className="text-background-50 font-heading text-base md:text-xl font-bold mt-4">
            第{episodeNumber}集 · {title}
          </p>
          <p className="text-secondary-300 text-xs md:text-sm mt-2 max-w-xl leading-relaxed">
            <i className="ri-error-warning-line mr-1"></i>
            本播放器为前端模拟演示，播放、暂停与进度均为展示效果，真实视频源待接入。
          </p>
        </div>

        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs flex items-center gap-1.5">
          <i className="ri-hd-line text-accent-400"></i> 高清演示
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 bg-background-100 border border-background-200 rounded-lg px-3 md:px-4 py-3">
        <button
          type="button"
          onClick={() => setPlaying((v) => !v)}
          aria-label={playing ? "暂停" : "播放"}
          className="w-9 h-9 shrink-0 rounded-md bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-lg`}></i>
        </button>
        <span className="text-xs text-foreground-600 w-11 shrink-0 tabular-nums">{formatTime(progress)}</span>
        <div className="flex-1 h-1.5 rounded-full bg-background-300 overflow-hidden">
          <div
            className="h-full bg-accent-500 transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <span className="text-xs text-foreground-400 w-11 shrink-0 tabular-nums text-right">{formatTime(100)}</span>
        <div className="hidden sm:flex items-center gap-2 text-foreground-500">
          <i className="ri-volume-up-line"></i>
          <i className="ri-fullscreen-line"></i>
        </div>
      </div>
    </div>
  );
}